import React from "react";
import ReactDOM from "react-dom";
import injectSheet from "react-jss";
import { withRouter, Redirect } from "react-router-dom";
import clsx from "clsx";
import { v4 as uuid } from "uuid";
import produce from "immer";
import { isEqual, cloneDeep } from "lodash";

import Toolbar from "../components/Toolbar";
import ButtonLink from "../components/ButtonLink";
import HotspotTypeLightbox from "../components/HotspotTypeLightbox";
import HotspotOptionsLightbox from "../components/HotspotOptionsLightbox";
import HotspotRenameLightbox from "../components/HotspotRenameLightbox";
import Player from "../../lib/player";
import Hotspot from "../../lib/player/src/hotspots/Hotspot";
import PinnedVideo from "../../lib/player/src/PinnedVideo";
import ContextMenu from "../components/ContextMenu";
import ContextMenuItem from "../components/ContextMenuItem";
import { formatName } from "../utils";
import HorizonLines from "../components/HorizonLines";
import CreateHotspot from "../components/hotspots/CreateHotspot";
import EditHotspot from "../components/hotspots/EditHotspot";
import Popups from "../../lib/player/src/popups/Popups";
import Tooltip from "../../lib/player/src/tooltips/PermaTooltip";
import { defaultIcons } from "../components/refresh/selectors/IconSelector";

const isDescendant = (parent, child) => {
  if (parent === child) {
    return true;
  }

  let node = child.parentNode;
  while (node !== null) {
    if (node === parent) {
      return true;
    }
    node = node.parentNode;
  }
  return false;
};

class TourView extends React.Component {
  constructor(props) {
    super(props);

    // Get the tour from the props
    const { activeImage, tour } = this.props;

    this.state = {
      tour: produce(tour, draft => {
        draft.data.startingImage = activeImage;
      }),
      addingHotspot: false,
      selectingHotspotType: false,
      selectingHotspotTypeLegacy: false,
      intersect: null,
      editingHotspot: false,
      editingHotspotLegacy: false,
      modifyHotspot: false,
      modifyHotspotLegacy: false,
      contextMenuType: "",
      currentHotspot: null,
      targetToolTip: "",
      showContextMenu: false,
      addingPinnedVideo: false,
      contextMenuPosition: {
        top: 0,
        left: 0,
      },
      contextMenuHotspot: null,
      helpText: "",
      imageName: "",
      startingRotation: {
        x: 0,
        y: 0,
        z: 0,
      },
      viewpointHorizonAngleX: 0,
      viewpointHorizonAngleY: 0,
      isChecked: false,
      horizonOffsetCurrent: 0,
    };

    this.onAddHotspot = this.onAddHotspot.bind(this);
    this.onHotspotSave = this.onHotspotSave.bind(this);
    this.onHotspotSaveLegacy = this.onHotspotSaveLegacy.bind(this);
    this.onEditHotspot = this.onEditHotspot.bind(this);
    this.getHotspotID = this.getHotspotID.bind(this);
    this.getHotspotTooltip = this.getHotspotTooltip.bind(this);
    this.onModifyHotspot = this.onModifyHotspot.bind(this);
    this.checkHoverHotspot = this.checkHoverHotspot.bind(this);
    this.updateSlider = this.updateSlider.bind(this);
    this.sliderMouseup = this.sliderMouseup.bind(this);
    this.sliderMousedown = this.sliderMousedown.bind(this);
    this.onDeleteClick = this.onDeleteClick.bind(this);
    this.onMoveHotspot = this.onMoveHotspot.bind(this);
    this.onSelectHotspotType = this.onSelectHotspotType.bind(this);
    this.onSelectHotspotTypeLegacy = this.onSelectHotspotTypeLegacy.bind(this);
  }

  componentDidUpdate(prevProps, prevState) {
    if (!isEqual(this.props.tour, prevProps.tour)) {
      // Get the tour from the props
      const { activeImage } = this.props;
      const data = Object.assign({}, this.props.tour.data, {
        startingImage: activeImage,
      });

      this.player.config = data;

      this.setState({ tour: this.props.tour });
    }

    if (
      prevState.currentHotspot &&
      this.state.currentHotspot &&
      prevState.currentHotspot.options.id !==
        this.state.currentHotspot.options.id
    ) {
      this.onEditHotspot();
    }
  }

  componentDidMount() {
    if (!this.content) return;

    const activeImage = this.props.tour.data.images[this.props.activeImage];

    if (activeImage.horizonOffsetX) {
      this.setState({
        viewpointHorizonAngleX: activeImage.horizonOffsetX,
      });
    } else {
      this.props.onUpdateImage(
        produce(activeImage, draft => {
          draft.horizonOffsetX = 0;
        }),
      );
    }

    if (activeImage.horizonOffsetZ) {
      this.setState({
        viewpointHorizonAngleZ: activeImage.horizonOffsetZ,
      });
    } else {
      this.props.onUpdateImage(
        produce(activeImage, draft => {
          draft.horizonOffsetZ = 0;
        }),
      );
    }

    if (activeImage.startingRotation) {
      this.setState({
        startingRotation: {
          x: activeImage.startingRotation.x,
          y: activeImage.startingRotation.y,
          z: activeImage.startingRotation.z,
        },
      });
    }

    // Create player
    this.player = new Player(this.content, this.state.tour, "editor");
    this.player.onRightClick = (event, hotspots, videos) => {
      // If hotspots are under mouse, show context menu
      if (hotspots.length > 0) {
        const hotspot = hotspots[0];
        this.setState({
          showContextMenu: true,
          contextMenuType: "Hotspot",
          contextMenuHotspot: hotspot,
          currentHotspot: hotspot._config,
          contextMenuPosition: {
            left: event.clientX,
            top: event.clientY,
          },
        });

        this.player.sceneManager.activeScene().setShowContextMenu(true);
        this.player.onMouseMove(event);
        this.player.onOneClick().then(e => {
          if (!isDescendant(ReactDOM.findDOMNode(this.contextMenu), e.target)) {
            this.setState({ showContextMenu: false });
            this.player.sceneManager.activeScene().setShowContextMenu(false);
          }
        });
      }

      if (videos.length > 0) {
        const video = videos[0];

        this.setState({
          showContextMenu: true,
          contextMenuType: "Video",
          contextMenuHotspot: video,
          contextMenuPosition: {
            left: event.clientX,
            top: event.clientY,
          },
        });

        this.player.sceneManager.activeScene().setShowContextMenu(true);
        this.player.onMouseMove(event);
        this.player.onOneClick().then(e => {
          if (!isDescendant(ReactDOM.findDOMNode(this.contextMenu), e.target)) {
            this.setState({ showContextMenu: false });
            this.player.sceneManager.activeScene().setShowContextMenu(false);
          }
        });
      }
    };

    this.player.onTransition = id => {
      this.props.onUpdateActiveImage(id);
    };
  }

  componentWillUnmount() {
    if (this.player) {
      this.player.dispose();
    }
  }

  getImageTitle() {
    return this.props.tour.data.images[this.props.activeImage].name || '';
  }

  onSetStartingPosition() {
    const rotation = this.player.getCurrentRotation();

    const oldImage = this.props.tour.data.images[this.props.activeImage];

    const startingPos = this.state.startingRotation;

    const newRotation = {
      x: rotation.x + startingPos.x,
      y: rotation.y + startingPos.y,
      z: rotation.z + startingPos.z,
    };

    if (newRotation.y > Math.PI) {
      newRotation.y -= 2 * Math.PI;
    } else if (newRotation.y < -1 * Math.PI) {
      newRotation.y += 2 * Math.PI;
    }
    if (newRotation.x > Math.PI / 2) {
      newRotation.x = Math.PI / 2;
    } else if (newRotation.x < (-1 * Math.PI) / 2) {
      newRotation.x = (-1 * Math.PI) / 2;
    }

    const image = Object.assign({}, oldImage, {
      startingRotation: {
        x: newRotation.x,
        y: newRotation.y,
        z: newRotation.z,
      },
    });

    this.props.onUpdateImage(image);

    window.alert(
      "Your viewpoint for this view has been set facing the direction as seen.\n\nTo reset, simply click Set Viewpoint when your view has been adjusted.",
    );
  }

  onSetStartingImage() {
    const data = Object.assign({}, this.props.tour.data, {
      startingImage: this.props.activeImage,
    });

    const tour = Object.assign({}, this.props.tour, {
      data,
    });

    this.props.onUpdateTour(tour);

    window.alert("This image has been set as the starting image");
  }

  onAddPinnedVideo() {
    this.setState({
      addingPinnedVideo: true,
      helpText:
        "Click on the four points for your video, starting top left and going clockwise.",
    });

    this.setVideoCoordinates([]);
  }

  setVideoCoordinates(coords) {
    this.player.onOneClick().then(event => {
      const intersect = this.player.intersectWithActivePhotoHolder(event)[0];

      this.player.addPinnedVideoMarker(intersect.uv);

      coords.push({ u: intersect.uv.x, v: intersect.uv.y });

      if (coords.length === 4) {
        this.setState({
          addingPinnedVideo: false,
          editingHotspotLegacy: true,
          helpText: "",
          currentHotspot: {
            uuid: uuid(),
            type: "pinned-video",
            options: {
              coordinates: coords,
            },
          },
        });
      } else {
        this.setVideoCoordinates(coords);
      }
    });
  }

  onAddHotspot() {
    this.setState({
      addingHotspot: true,
      helpText: "Click on location to add new hotspot.",
    });

    this.player.onOneClick().then(event => {
      if (this.checkHoverHotspot(event)) {
        this.setState({
          addingHotspot: false,
          helpText: "",
        });
        window.alert("You can not add hotspot above existing one!");
        return;
      }

      this.setState({ addingHotspot: false, helpText: "" });

      const intersect = this.player.intersectWithActivePhotoHolder(event)[0];

      console.log("intersect", intersect);

      const { uv } = intersect;

      // Choose type of hotspot
      this.setState({ selectingHotspotType: true, intersect: uv });

      const imageName =
        this.props.tour.data.images[this.props.activeImage].name || '';
      this.setState({
        currentHotspot: {
          uuid: uuid(),
          u: uv.x,
          v: uv.y,
          ratio: 0,
          options: {},
          tooltip: imageName,
        },
      });

      this.setState({
        targetToolTip: this.props.tour.data.images[this.props.activeImage].name || '',
      });
    });
  }

  onEditHotspot() {
    const target_id = this.state.currentHotspot.options.id;

    if (!target_id) {
      return;
    }

    const target_tooltip = this.props.tour.data.images[target_id].name || '';

    const new_hotspot = {
      ...this.state.currentHotspot,
      tooltip: target_tooltip,
    };
    if (this.state.editingHotspotLegacy) {
      this.setState({ currentHotspot: new_hotspot });
    }
  }

  onSelectHotspotTypeLegacy(type) {
    this.setState({
      selectingHotspotTypeLegacy: false,
      editingHotspotLegacy: true,
      currentHotspot: { ...this.state.currentHotspot, type },
    });
  }

  onSelectHotspotType(type) {
    this.setState({
      selectingHotspotType: false,
      editingHotspot: true,
      currentHotspot: { ...this.state.currentHotspot, type },
    });
  }

  calculateOffset(startingRotation) {
    let offset = 0;
    if (startingRotation) {
      let startingRot = startingRotation.y;
      startingRot = (startingRot * -1) / Math.PI / 2;
      if (startingRot < 0) {
        startingRot += 1;
      }
      offset = startingRot;
    }

    return offset;
  }

  addBoomerang(image, hotspot, ratio) {
    // add boomerang
    const uuid = hotspot.uuid;
    const id = image.id;

    const target_id = hotspot.options.id;
    let target_old_image = null;
    const images = this.props.tour.data.images;
    for (let key in images) {
      if (images[key].id === target_id) {
        target_old_image = images[key];
        break;
      }
    }
    let rotation_start = 0;
    let rotation_target = 0;
    if (image.startingRotation) {
      rotation_start = image.startingRotation.y / Math.PI / 2;
    }

    if (target_old_image.startingRotation) {
      rotation_target = target_old_image.startingRotation.y / Math.PI / 2;
    }

    rotation_target -= rotation_start;

    // noSpin states if the new hotspot should be placed in the same position as the non-boomerang one (i.e the original)
    // so that you're still looking at it after transition (as apposed to it being behind you)
    const noSpin =
      hotspot.type === "fade" ||
      hotspot.type === "elevate-up" ||
      hotspot.type === "elevate-down";

    let new_u = hotspot.u + rotation_target + (noSpin ? 0 : 0.5);
    if (!noSpin) {
      new_u %= 1;
    }

    if (new_u < 0) {
      new_u += 1;
    }

    let newRatio = 1.5 - new_u;
    if (newRatio > 1) {
      newRatio -= 1;
    } else if (newRatio < 0) {
      newRatio += 1;
    }

    this.setState({ currentHotspot: hotspot });

    let newType = hotspot.type;
    let newIcon = hotspot.icon;

    if (hotspot.type === "elevate-up") {
      newType = "elevate-down";
      newIcon = defaultIcons.find(x => x.title === "elevate-down");
    }

    if (hotspot.type === "elevate-down") {
      newType = "elevate-up";
      newIcon = defaultIcons.find(x => x.title === "elevate-up");
    }

    const target_hotspot = {
      ...hotspot,
      icon: newIcon,
      type: newType,
      options: {
        ...hotspot.options,
        id,
        ratio,
        type: 0,
      },
      uuid,
      u: new_u,
      v: noSpin ? hotspot.v : 1 - hotspot.v,
      ratio: newRatio,
      tooltip: hotspot.targetToolTip ?? this.state.targetToolTip,
    };
    // console.log("START TARGET", target_hotspot);
    const target_oldHotspots = target_old_image.hotspots
      ? target_old_image.hotspots
      : [];
    const target_image = Object.assign({}, target_old_image, {
      hotspots: [...target_oldHotspots, target_hotspot],
    });

    this.props.onUpdateBoomerang(image, target_image);
  }

  onHotspotSave(current) {
    // Add hotspot to current image
    const image = cloneDeep(
      this.props.tour.data.images[this.props.activeImage],
    );
    image.hotspots ??= [];

    let ratio = 0.5 - current.u;
    if (image.startingRotation) {
      let startingRotation = -image.startingRotation.y;
      if (startingRotation < 0) {
        startingRotation += Math.PI;
      }
      startingRotation /= Math.PI;
      ratio -= startingRotation;
    }

    ratio = 1.5 - current.u;
    if (ratio > 1) {
      ratio -= 1;
    } else if (ratio < 0) {
      ratio += 1;
    }

    const newHotspot = produce(current, draft => {
      draft.ratio = ratio;
      draft.addOffset = true;
      draft.options.offset = 0;
    });

    image.hotspots.push(newHotspot);

    this.setState({
      editingHotspot: false,
    });

    this.player.sceneManager.activeScene().addHotspotFromConfig(newHotspot);

    if (!newHotspot.boomerang) {
      this.props.onUpdateImage(image);
    } else {
      this.addBoomerang(image, newHotspot, ratio);
    }
  }

  onHotspotSaveLegacy() {
    // legacy hotspots should use the old code
    // this includes pinned videos
    // Check if this is a pinned video first
    if (this.state.currentHotspot.type === "pinned-video") {
      const videoConfig = {
        coordinates: this.state.currentHotspot.options.coordinates,
        uuid: this.state.currentHotspot.uuid,
        url: this.state.currentHotspot.options.url,
      };

      const video = new PinnedVideo(videoConfig);
      this.player.sceneManager.activeScene().addPinnedVideo(video);

      this.setState({ editingHotspotLegacy: false });

      const data = Object.assign({}, this.props.tour.data, {
        startingImage: this.props.activeImage,
      });

      const tour = Object.assign({}, this.props.tour, {
        data,
      });

      // Add hotspot to current image
      const oldImage = tour.data.images[this.props.activeImage];
      const oldVideos = oldImage.pinnedVideos ? oldImage.pinnedVideos : [];

      const image = Object.assign({}, oldImage, {
        pinnedVideos: [...oldVideos, videoConfig],
      });

      this.props.onUpdateImage(image);
    } else {
      // Check tooltip not empty
      if (
        this.state.currentHotspot.tooltip === "" ||
        this.state.currentHotspot.tooltip === " "
      ) {
        return;
      }

      const data = Object.assign({}, this.props.tour.data, {
        startingImage: this.props.activeImage,
      });

      const tour = Object.assign({}, this.props.tour, {
        data,
      });

      // Add hotspot to current image
      const oldImage = tour.data.images[this.props.activeImage];

      const oldHotspots = oldImage.hotspots ? oldImage.hotspots : [];

      let ratio = 0.5 - this.state.currentHotspot.u;
      if (oldImage.startingRotation) {
        let startingRotation = -oldImage.startingRotation.y;
        if (startingRotation < 0) {
          startingRotation += Math.PI;
        }
        startingRotation /= Math.PI;
        ratio -= startingRotation;
      }

      ratio = 1.5 - this.state.currentHotspot.u;
      if (ratio > 1) {
        ratio -= 1;
      } else if (ratio < 0) {
        ratio += 1;
      }

      const new_hotspot = produce(this.state.currentHotspot, draft => {
        draft.ratio = ratio;
        draft.addOffset = true;
        draft.options.offset = 0;
      });

      const image = Object.assign({}, oldImage, {
        hotspots: [...oldHotspots, new_hotspot],
      });

      this.setState({
        editingHotspotLegacy: false,
      });

      this.player.sceneManager.activeScene().addHotspotFromConfig(new_hotspot);

      if (!this.state.isChecked) {
        this.props.onUpdateImage(image);
        return;
      }

      this.addBoomerang(image, new_hotspot, ratio);
    }
  }

  getHotspotID() {
    const image = this.state.tour.data.images[this.props.activeImage];

    if (image.hotspots && this.state.contextMenuHotspot) {
      return image.hotspots.findIndex(
        hotspot => hotspot.uuid === this.state.contextMenuHotspot.getUUID(),
      );
    } else {
      return -1;
    }
  }

  getHotspotTooltip() {
    let tooltip = "";
    const index = this.getHotspotID();

    if (index === -1) {
      return tooltip;
    }

    const image = this.state.tour.data.images[this.props.activeImage];
    tooltip = image.hotspots[index].tooltip
      ? image.hotspots[index].tooltip
      : "";

    return tooltip;
  }

  onModifyHotspot(hotspot) {
    hotspot ??= this.state.currentHotspot;
    // Stickers don't require a tooltip; other hotspot types do
    const canSave = hotspot.tooltip || hotspot.type === "sticker";
    if (canSave) {
      const image = cloneDeep(
        this.props.tour.data.images[this.props.activeImage],
      );
      const index = this.getHotspotID();
      if (index === -1) {
        return;
      }

      image.hotspots[index] = hotspot;

      const config = Hotspot.fromConfig(this.player, hotspot);
      this.player.sceneManager.activeScene().updateHotspot(config);
      this.props.onUpdateImage(image);
      this.setState({ modifyHotspot: false });
    }
  }

  checkHoverHotspot(event) {
    return this.player.checkHoverHotspot(event);
  }

  onMoveHotspot() {
    this.setState({
      addingHotspot: true,
      helpText: "Click on location to move hotspot.",
      showContextMenu: false,
    });

    this.player.onOneClick().then(event => {
      if (this.checkHoverHotspot(event)) {
        this.setState({
          addingHotspot: false,
          helpText: "",
        });
        window.alert("You can not add hotspot above existing one!");
        return;
      }

      this.setState({ addingHotspot: false, helpText: "" });

      const intersect = this.player.intersectWithActivePhotoHolder(event)[0];

      const { uv } = intersect;

      const newHotspot = {
        ...this.state.currentHotspot,
        u: uv.x,
        v: uv.y,
      };

      this.setState({
        currentHotspot: newHotspot,
      });

      const image = cloneDeep(
        this.state.tour.data.images[this.props.activeImage],
      );
      const index = this.getHotspotID();
      if (index === -1) {
        console.warn("Hotspot not found");
        return;
      }

      image.hotspots[index] = newHotspot;

      const config = Hotspot.fromConfig(this.player, newHotspot);
      this.player.sceneManager.activeScene().updateHotspot(config);
      this.props.onUpdateImage(image);
    });
  }

  onDeleteClick() {
    if (this.state.contextMenuHotspot._video !== undefined) {
      this.player.sceneManager
        .activeScene()
        .removePinnedVideo(this.state.contextMenuHotspot);
      this.setState({ showContextMenu: false });
      this.player.sceneManager.activeScene().setShowContextMenu(false);

      const oldImage = this.state.tour.data.images[this.props.activeImage];

      const image = Object.assign({}, oldImage, {
        pinnedVideos: oldImage.pinnedVideos.filter(
          item => item.uuid !== this.state.contextMenuHotspot.getUUID(),
        ),
      });

      this.props.onUpdateImage(image);
    } else {
      this.player.sceneManager
        .activeScene()
        .removeHotspot(this.state.contextMenuHotspot);
      this.setState({ showContextMenu: false });
      this.player.sceneManager.activeScene().setShowContextMenu(false);

      const oldImage = this.state.tour.data.images[this.props.activeImage];
      // console.log("IMAGE", oldImage);
      const image = Object.assign({}, oldImage, {
        hotspots: oldImage.hotspots.filter(
          item => item.uuid !== this.state.contextMenuHotspot.getUUID(),
        ),
      });
      this.props.onUpdateImage(image);
    }
  }

  content;
  contextMenu;
  player;

  render() {
    const {
      tour,
      classes,
      history,
      activeImage,
      onSaveTour,
      onEmbedTour,
      onTourSettings,
      onUpdateTour,
    } = this.props;

    const {
      addingHotspot,
      selectingHotspotType,
      selectingHotspotTypeLegacy,
      editingHotspot,
      editingHotspotLegacy,
      modifyHotspot,
      modifyHotspotLegacy,
      currentHotspot,
      targetToolTip,
      contextMenuPosition,
      addingPinnedVideo,
      showContextMenu,
      helpText,
      contextMenuType,
      contextMenuHotspot,
    } = this.state;

    if (!activeImage) {
      return (
        <Redirect to={{ pathname: history.location.pathname.slice(0, -4) }} />
      );
    }

    return (
      <div className={classes.container}>
        <Popups
          player={this.player}
          setAudioClip={({ name }) => alert(`Now playing: ${name}`)}
        />
        <Tooltip player={this.player} />
        <CreateHotspot
          show={selectingHotspotType}
          onClose={() => this.setState({ selectingHotspotType: false })}
          onSelect={this.onSelectHotspotType}
          switchToLegacy={() =>
            this.setState({
              selectingHotspotType: false,
              selectingHotspotTypeLegacy: true,
            })
          }
        />
        <EditHotspot
          show={editingHotspot || modifyHotspot}
          editing={modifyHotspot}
          type={currentHotspot?.type}
          account={tour.account}
          incomingScenes={Object.values(tour.data.images ?? {}).filter(x =>
            x.hotspots.reduce(
              (acc, curr) =>
                acc ||
                (["link", "elevate-up", "elevate-down", "fade"].includes(
                  curr.type,
                ) &&
                  curr?.options?.id === activeImage),
              false,
            ),
          )}
          event={tour.team}
          hotspot={currentHotspot}
          images={tour.data.images ?? []}
          activeImage={activeImage}
          isLink={(tour.data.links ?? []).find(
            link =>
              link.type === "real" &&
              link.scene === activeImage &&
              link.hotspot === currentHotspot?.uuid,
          )}
          setLink={value => {
            if (value) {
              onUpdateTour(
                produce(tour, draft => {
                  draft.data.links ||= [];
                  draft.data.links.push({
                    type: "real",
                    id: Date.now(),
                    scene: activeImage,
                    hotspot: currentHotspot.uuid,
                  });
                }),
              );
            } else {
              onUpdateTour(
                produce(tour, draft => {
                  draft.data.links ||= [];
                  draft.data.links = draft.data.links.filter(
                    link =>
                      !(
                        link.scene === activeImage &&
                        link.hotspot === currentHotspot.uuid
                      ),
                  );
                }),
              );
            }
          }}
          onClose={() =>
            this.setState({
              editingHotspot: false,
              modifyHotspot: false,
            })
          }
          targetToolTip={targetToolTip}
          onComplete={config => {
            this.setState({ currentHotspot: config });
            if (modifyHotspot) {
              this.onModifyHotspot(config);
            } else {
              this.onHotspotSave(config);
            }
          }}
        />
        <HotspotTypeLightbox
          show={selectingHotspotTypeLegacy}
          onSelect={this.onSelectHotspotTypeLegacy}
          onClose={() => this.setState({ selectingHotspotTypeLegacy: false })}
        />
        <HotspotOptionsLightbox
          show={editingHotspotLegacy}
          hotspot={currentHotspot}
          target={targetToolTip}
          isBoomerang={this.state.isChecked}
          images={this.state.tour.data.images}
          onClose={() => this.setState({ editingHotspotLegacy: false })}
          getOnChangeHandler={name => raw => {
            let value = raw;
            if (raw.target !== undefined) {
              value = raw.target.value;
            }

            if (name === "tooltip" || name === "title") {
              value = formatName(value);
            }

            if (name === "tooltip") {
              this.setState(
                produce(draft => {
                  draft.currentHotspot.tooltip = value;
                }),
              );
            } else if (name === "checkbox") {
              this.setState({ isChecked: value });
            } else if (name === "permaTooltip") {
              this.setState(
                produce(draft => {
                  draft.currentHotspot.permaTooltip = value;
                }),
              );
            } else if (name === "target") {
              this.setState({ targetToolTip: value });
            } else if (name === "gallery") {
              this.setState(
                produce(draft => {
                  draft.currentHotspot.options.content = value;
                }),
              );
            } else {
              this.setState(
                produce(draft => {
                  draft.currentHotspot.options[name] = value;
                  draft.currentHotspot.options.ratio = Math.random();
                  draft.currentHotspot.options.type = 1;
                }),
              );
            }
          }}
          onComplete={this.onHotspotSaveLegacy}
        />
        {modifyHotspotLegacy && (
          <HotspotRenameLightbox
            show={modifyHotspotLegacy}
            hotspot={currentHotspot}
            images={this.state.tour.data.images}
            onClose={() => this.setState({ modifyHotspotLegacy: false })}
            onComplete={this.onModifyHotspot}
            getOnChangeHandler={name => raw => {
              let value = raw;
              if (raw.target !== undefined) {
                value = raw.target.value;
              }

              if (name === "tooltip" || name === "title") {
                value = formatName(value);
              }

              if (name === "tooltip" || name === "permaTooltip") {
                this.setState({
                  currentHotspot: {
                    ...this.state.currentHotspot,
                    [name]: value,
                  },
                });
              } else {
                this.setState({
                  currentHotspot: {
                    ...this.state.currentHotspot,
                    options: {
                      ...this.state.currentHotspot.options,
                      [name]: value,
                      ratio: Math.random(),
                      type: 1,
                    },
                  },
                });
              }
            }}
          />
        )}
        {showContextMenu && (
          <ContextMenu
            position={contextMenuPosition}
            ref={ref => {
              this.contextMenu = ref;
            }}
          >
            {[
              "media",
              "web",
              "link",
              "elevate-up",
              "elevate-down",
              "fade",
              "audio",
              "gallery",
              "poi",
              "timeline",
              "sticker",
            ].includes(contextMenuHotspot._config?.type) && (
              <ContextMenuItem
                onClick={() => {
                  if (contextMenuHotspot._video === undefined) {
                    this.setState({
                      modifyHotspot: true,
                      showContextMenu: false,
                    });
                    this.player.sceneManager
                      .activeScene()
                      .setShowContextMenu(false);
                  }
                }}
              >
                Edit {contextMenuType}
              </ContextMenuItem>
            )}
            {[
              "link",
              "elevate-up",
              "elevate-down",
              "fade",
              "audio",
              "gallery",
              "poi",
              "timeline",
              "web",
              "note",
              "video",
            ].includes(contextMenuHotspot._config?.type) && (
              <ContextMenuItem
                onClick={() => {
                  if (contextMenuHotspot._video === undefined) {
                    this.setState({
                      modifyHotspotLegacy: true,
                      showContextMenu: false,
                    });
                    this.player.sceneManager
                      .activeScene()
                      .setShowContextMenu(false);
                  }
                }}
              >
                Edit {contextMenuType} (legacy)
              </ContextMenuItem>
            )}
            {contextMenuType === "Hotspot" && (
              <ContextMenuItem onClick={this.onMoveHotspot}>
                Move Hotspot
              </ContextMenuItem>
            )}
            <ContextMenuItem onClick={this.onDeleteClick}>
              Delete {contextMenuType}
            </ContextMenuItem>
          </ContextMenu>
        )}
        <Toolbar
          onSaveTour={onSaveTour}
          onEmbedTour={onEmbedTour}
          onTourSettings={onTourSettings}
          renderLeft={({ actionClasses }) => (
            <div className={actionClasses.row}>
              <ButtonLink
                className={actionClasses.action}
                icon={["fal", addingHotspot ? "crosshairs" : "plus"]}
                name={addingHotspot ? "Click to add hotspot" : "Add a hotspot"}
                onClick={() => this.onAddHotspot()}
              />
              <ButtonLink
                className={actionClasses.action}
                icon={["fal", "video"]}
                name={
                  addingPinnedVideo ? "Click to add video" : "Add pinned video"
                }
                onClick={() => this.onAddPinnedVideo()}
              />
              <ButtonLink
                className={actionClasses.action}
                icon={["fal", "eye"]}
                name="Set viewpoint"
                onClick={() => this.onSetStartingPosition()}
              />
              <ButtonLink
                className={actionClasses.action}
                icon={["fal", "play"]}
                name="Set as starting image"
                onClick={() => this.onSetStartingImage()}
              />
            </div>
          )}
        />
        {helpText.length > 0 && (
          <div className={classes.helpText}>{helpText}</div>
        )}
        <div
          className={clsx(classes.mainContainer, {
            [classes.addingHotspot]: addingHotspot,
          })}
          ref={content => {
            this.content = content;
          }}
        >
          <HorizonLines horizonOffset={this.state.horizonOffsetCurrent} />
        </div>
        <Toolbar
          hideRightActions
          renderLeft={({ actionClasses }) => (
            <ButtonLink
              active
              className={actionClasses.action}
              icon={["fal", "chevron-left"]}
              name="Back"
              onClick={() =>
                history.replace(history.location.pathname.slice(0, -4))
              }
            />
          )}
          renderRight={({ actionClasses }) => (
            <div className={actionClasses.row}>
              <div className={actionClasses.action}>
                Horizon Offset {this.state.horizonOffsetCurrent}deg{" "}
              </div>
              <input
                className={actionClasses.actionSlider}
                type="range"
                min="-100"
                max="100"
                value={this.state.horizonOffsetCurrent * 10}
                onChange={this.updateSlider}
                onMouseDown={this.sliderMousedown}
                onMouseUp={this.sliderMouseup}
              />
              <div className={actionClasses.action}>{this.getImageTitle()}</div>
            </div>
          )}
        />
      </div>
    );
  }

  sliderMousedown() {
    const image = this.props.tour.data.images[this.props.activeImage];
    this.setState({
      horizonOffsetXOld: image.horizonOffsetX,
      horizonOffsetZOld: image.horizonOffsetZ,
    });
  }

  sliderMouseup() {
    setTimeout(() => {
      this.setState({ horizonOffsetCurrent: 0 });
    }, 100);
  }

  updateSlider(evt) {
    // Divide num by 10 to give more resolution
    const value = evt.target.value / 10;
    this.setState({ horizonOffsetCurrent: value });

    const image = this.props.tour.data.images[this.props.activeImage];

    const rotation = this.player.getCurrentRotation()._y;

    const offset = {
      x: Math.cos(rotation) * value,
      z: Math.sin(rotation) * value,
    };

    this.props.onUpdateImage(
      produce(image, draft => {
        // calculate X rotation
        draft.horizonOffsetX = this.state.horizonOffsetXOld + offset.x;
        offset.x = this.state.horizonOffsetXOld + offset.x;

        // calculate Z rotation
        draft.horizonOffsetZ = this.state.horizonOffsetZOld + offset.z;
        offset.z = this.state.horizonOffsetZOld + offset.z;
      }),
    );

    // Display offset in the player
    this.player.onHorizonOffsetUpdate(offset);
  }
}

const styles = {
  container: {
    width: "100%",
    height: "100vh",
    display: "flex",
    "flex-direction": "column",
  },
  mainContainer: {
    position: "relative",
    display: "flex",
    flex: 1,
    overflow: "hidden",
    "background-color": "#eee",
  },
  addingHotspot: {
    cursor: "crosshair",
  },
  helpText: {
    position: "absolute",
    top: "60px",
    left: 0,
    width: "100%",
    "text-align": "center",
    color: "white",
    "font-size": "18px",
    "font-weight": "bold",
    "z-index": 2,
    "background-color": "rgba(0, 0, 0, 0.5)",
    padding: "8px",
    "box-sizing": "border-box",
    "pointer-events": "none",
  },
};

export default withRouter(injectSheet(styles)(TourView));
